import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

interface Props {
  name: string;
  label: string;
}

export const options = [
  {
    label: "A",
    value: "a",
    text:
      "My hip pain is so severe that it will stop me from walking, shopping, running or other weight bearing exercise.",
    answer:
      "Your hip pain is so severe that it will stop you from weight bearing activity. You do:",
  },
  {
    label: "B",
    value: "b",
    text:
      "My hip pain is present with exercise, but it does not stop me from walking, shopping, running or other weight bearing type exercise.",
    answer:
      "Your hip pain is present with exercise, but does not stop you. You do:",
  },
  {
    label: "C",
    value: "c",
    text:
      "If you have no pain while you undertake walking, shopping, running or other weight bearing type exercise.",
    answer: "You have no pain while doing weight bearing activity. You do:",
  },
];

export const scaleLabelsMap = {
  a: [
    "I do not undertake any extra activity on my legs - I only move about the house.",
    "I do less than 10 minutes.",
    "I do 10 – 19 minutes.",
    "I do 20 – 29 minutes.",
    "I do more than 30 minutes.",
  ],
  b: [
    "I do not undertake any extra activity on my legs - I only move about the house.",
    "I do less than 10 minutes.",
    "I do 10 – 19 minutes.",
    "I do 20 – 29 minutes.",
    "I do more than 30 minutes.",
  ],
  c: [
    "I do not undertake any extra activity on my legs - I only move about the house.",
    "I do less than 10 minutes.",
    "I do 10 – 19 minutes.",
    "I do 20 – 29 minutes.",
    "I do more than 30 minutes.",
  ],
};

export const pointsMap = {
  a: [0, 2, 5, 7, 10],
  b: [0, 5, 10, 15, 20],
  c: [6, 12, 18, 24, 30],
};

export function getVisaG8LabelFromValue(value: number) {
  for (const branch of ["a", "b", "c"] as const) {
    const idx = pointsMap[branch].indexOf(value);
    if (idx !== -1) {
      return {
        branch,
        label: scaleLabelsMap[branch][idx],
        value,
      };
    }
  }
  return null;
}

const VisaG8 = ({ name }: Props) => {
  const { control } = useFormContext();
  const [selectedOption, setSelectedOption] = useState<"a" | "b" | "c" | null>(
    null,
  );

  return (
    <Card>
      <CardContent className="p-4">
        <FormField
          control={control}
          name={name}
          render={({ field }) => (
            <FormItem className="space-y-6">
              <FormLabel>
                <Label>
                  8. Please complete EITHER A, B or C in this question.
                </Label>
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={(value: "a" | "b" | "c") => {
                    setSelectedOption(value);
                    field.onChange(null);
                  }}
                  value={selectedOption || undefined}
                  className="space-y-4"
                >
                  {options.map((option) => (
                    <div
                      key={option.value}
                      className={cn(
                        "flex flex-col items-start p-4 border rounded-lg",
                        selectedOption === option.value
                          ? "bg-indigo-100"
                          : "bg-slate-100 text-slate-700",
                      )}
                    >
                      <div className="flex flex-row justify-start items-center gap-x-3">
                        <RadioGroupItem
                          value={option.value}
                          id={`option-${option.value}`}
                          className="mt-1"
                        />
                        <div className="flex-grow">
                          <Label
                            htmlFor={`option-${option.value}`}
                            className="font-medium"
                          >
                            {option.label}. {option.text}
                          </Label>
                        </div>
                      </div>
                      {selectedOption === option.value && (
                        <div className="mt-4">
                          <Label className="block mb-2">
                            How much of this activity do you do each day?
                          </Label>
                          <ul className="flex flex-col justify-start items-start gap-y-2">
                            {scaleLabelsMap[selectedOption].map((
                              label,
                              index,
                            ) => (
                              <li
                                key={`scale-option-${label}`}
                                className="flex flex-row justify-start items-center gap-x-4"
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    field.onChange(
                                      pointsMap[selectedOption][index],
                                    )}
                                  className={cn(
                                    "rounded-md h-12 w-20 px-2 text-lg transition-all duration-300 ease-in-out",
                                    "focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                                    field.value ===
                                        pointsMap[selectedOption][index]
                                      ? "bg-indigo-600 text-white"
                                      : "bg-slate-200 text-slate-700 hover:bg-slate-300",
                                  )}
                                >
                                  {pointsMap[selectedOption][index]}
                                </button>
                                <span className="w-full text-md text-slate-600">
                                  {label}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
};

export default VisaG8;
